import React, { useState, useEffect, useCallback, useContext } from "react";
import { AuthContext } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip,
  Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  Users, Shield, DollarSign, Zap, Home, Wheat, Brain,
  AlertTriangle, Settings2, Crown, Loader2, RefreshCw,
  Save, BarChart3, PieChart as PieChartIcon, Bell, CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = { haven: "#10b981", harvest: "#f59e0b", other: "#6366f1" };
const ROLE_COLORS = {
  owner: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  admin: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  developer: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  viewer: "bg-gray-500/15 text-gray-400 border-gray-500/30",
};
const ROLE_ICONS = { owner: Crown, admin: Shield, developer: Users, viewer: Users };

// ==================== Loading Skeleton ====================
function LoadingSkeleton() {
  return (
    <div className="space-y-5 animate-pulse" data-testid="admin-loading">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-4 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-[var(--qn-surface)]" />
              <div className="h-2 w-20 bg-[var(--qn-surface)] rounded" />
            </div>
            <div className="h-6 w-16 bg-[var(--qn-surface)] rounded mb-1" />
            <div className="h-2 w-12 bg-[var(--qn-surface)] rounded" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-64 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-4">
          <div className="h-4 w-32 bg-[var(--qn-surface)] rounded mb-4" />
          <div className="h-40 bg-[var(--qn-surface)] rounded" />
        </div>
        <div className="h-64 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-4">
          <div className="h-4 w-32 bg-[var(--qn-surface)] rounded mb-4" />
          <div className="h-40 bg-[var(--qn-surface)] rounded" />
        </div>
      </div>
    </div>
  );
}

// ==================== Error State ====================
function ErrorState({ message, onRetry }) {
  return (
    <div className="py-24 text-center" data-testid="admin-error">
      <AlertTriangle className="w-12 h-12 text-red-400/30 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-[var(--qn-foreground)] mb-2">Unable to Load Data</h3>
      <p className="text-sm text-[var(--qn-foreground-muted)] mb-4">{message || "An error occurred while loading tenant admin data."}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />Try Again
        </Button>
      )}
    </div>
  );
}

// ==================== Overview Tab ====================
function OverviewTab({ overview, budget, onRefresh, loading, error }) {
  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState message={error} onRetry={onRefresh} />;
  if (!overview) return <ErrorState message="No overview data available" onRetry={onRefresh} />;

  const hasNoData = overview.total_executions === 0 && overview.member_count === 1;
  const verticalData = [
    { name: "HAVEN", value: overview.verticals.haven.executions, fill: COLORS.haven },
    { name: "HARVEST", value: overview.verticals.harvest.executions, fill: COLORS.harvest },
    { name: "Custom", value: overview.verticals.other.executions, fill: COLORS.other },
  ];

  const budgetPct = budget?.utilization_pct || 0;
  const budgetColor = budgetPct >= 90 ? "#ef4444" : budgetPct >= 75 ? "#f59e0b" : "#10b981";

  return (
    <div className="space-y-5" data-testid="admin-overview-tab">
      {hasNoData && (
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-400 flex items-center gap-2">
          <Bell className="w-4 h-4 shrink-0" />
          <span>Your organization is set up! Start inviting team members and running swarms to see usage stats here.</span>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Users, label: "Team Members", value: overview.member_count, color: "#3b82f6", sub: `${overview.pending_invites} pending` },
          { icon: Zap, label: "Total Executions", value: overview.total_executions.toLocaleString(), color: "#8b5cf6" },
          { icon: DollarSign, label: "Total Spend", value: `$${overview.total_cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: "#f59e0b" },
          { icon: BarChart3, label: "Total Tokens", value: overview.total_tokens.toLocaleString(), color: "#06b6d4" },
        ].map((s) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color}18` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[var(--qn-foreground-muted)]">{s.label}</span>
            </div>
            <div className="text-xl font-outfit font-bold text-[var(--qn-foreground)]">{s.value}</div>
            {s.sub && <div className="text-[10px] text-[var(--qn-foreground-subtle)] mt-1">{s.sub}</div>}
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="admin-vertical-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">Executions by Vertical</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={verticalData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                  {verticalData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <RTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: 10, color: "var(--qn-foreground-muted)" }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="admin-budget-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">Budget Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            {budget?.monthly_budget ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--qn-foreground)]">${budget.current_spend.toLocaleString("en-US", { minimumFractionDigits: 2 })} / ${budget.monthly_budget.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  <span className="text-sm font-bold" style={{ color: budgetColor }}>{budgetPct}%</span>
                </div>
                <Progress value={Math.min(budgetPct, 100)} className="h-3" />
                {budget.triggered_alerts?.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Alert thresholds triggered: {budget.triggered_alerts.join("%, ")}%
                  </div>
                )}
                {budget.auto_pause_at_limit && (
                  <div className="flex items-center gap-2 text-[10px] text-[var(--qn-foreground-muted)]">
                    <Shield className="w-3 h-3" /> Auto-pause enabled at budget limit
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-8 h-8 text-[var(--qn-foreground-muted)]/30 mx-auto mb-2" />
                <p className="text-xs text-[var(--qn-foreground-muted)]">No budget configured. Set one in the Budget tab.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== Members Tab ====================
function MembersTab({ onRefresh }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQuota, setEditingQuota] = useState(null);
  const [quotaExec, setQuotaExec] = useState("");
  const [quotaCost, setQuotaCost] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/org/members`).then(r => setMembers(r.data.members || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveQuota = async (memberId) => {
    // Validate inputs
    const execLimit = quotaExec ? parseInt(quotaExec) : null;
    const costLimit = quotaCost ? parseFloat(quotaCost) : null;

    if (execLimit !== null && (isNaN(execLimit) || execLimit < 0)) {
      toast.error("Execution limit must be a positive number");
      return;
    }
    if (costLimit !== null && (isNaN(costLimit) || costLimit < 0)) {
      toast.error("Cost limit must be a positive number");
      return;
    }
    if (execLimit === null && costLimit === null) {
      toast.error("Please set at least one limit");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/org/members/${memberId}/quota`, {
        monthly_execution_limit: execLimit,
        monthly_cost_limit: costLimit,
      });
      toast.success("Quota updated successfully");
      setEditingQuota(null);
      const res = await axios.get(`${API}/admin/org/members`);
      setMembers(res.data.members || []);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update quota");
    }
    setSaving(false);
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-[var(--qn-foreground-muted)]" />Loading members...</div>;

  return (
    <div className="space-y-3" data-testid="admin-members-tab">
      {members.map((m) => {
        const RoleIcon = ROLE_ICONS[m.role] || Users;
        const isEditing = editingQuota === m.user_id;
        return (
          <motion.div key={m.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] overflow-hidden"
            data-testid={`admin-member-${m.user_id}`}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[var(--qn-surface)] flex items-center justify-center text-sm font-bold text-[var(--qn-foreground)]">
                    {(m.name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[var(--qn-foreground)]">{m.name}</div>
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">{m.email}</div>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${ROLE_COLORS[m.role] || ""}`}>
                    <RoleIcon className="w-3 h-3 mr-1" />{m.role}
                  </Badge>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                  if (isEditing) { setEditingQuota(null); } else {
                    setEditingQuota(m.user_id);
                    setQuotaExec(m.quota?.monthly_execution_limit?.toString() || "");
                    setQuotaCost(m.quota?.monthly_cost_limit?.toString() || "");
                  }
                }} data-testid={`admin-edit-quota-${m.user_id}`}>
                  <Settings2 className="w-3 h-3 mr-1" />{isEditing ? "Cancel" : "Set Quota"}
                </Button>
              </div>

              <div className="grid grid-cols-5 gap-3 text-center">
                {[
                  { label: "Executions", value: m.stats.total_executions },
                  { label: "HAVEN", value: m.stats.haven_executions, color: COLORS.haven },
                  { label: "HARVEST", value: m.stats.harvest_executions, color: COLORS.harvest },
                  { label: "Cost", value: `$${m.stats.total_cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                  { label: "Tokens", value: m.stats.total_tokens.toLocaleString() },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">{s.label}</div>
                    <div className="text-sm font-bold font-mono" style={{ color: s.color || "var(--qn-foreground)" }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {m.quota && !isEditing && (
                <div className="mt-3 flex items-center gap-4 text-[10px] text-[var(--qn-foreground-muted)] p-2 rounded-lg bg-[var(--qn-surface)]/30">
                  <span>Quota:</span>
                  {m.quota.monthly_execution_limit && <span>Max {m.quota.monthly_execution_limit} executions/mo</span>}
                  {m.quota.monthly_cost_limit && <span>Max ${m.quota.monthly_cost_limit}/mo</span>}
                </div>
              )}

              <AnimatePresence>
                {isEditing && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="mt-3 p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]/20">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-[var(--qn-foreground-muted)] mb-1 block">Monthly Execution Limit</label>
                        <Input type="number" placeholder="e.g., 500" value={quotaExec} onChange={(e) => setQuotaExec(e.target.value)}
                          className="bg-[var(--qn-background)] border-[var(--qn-border)] h-8 text-xs" data-testid={`admin-quota-exec-${m.user_id}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-[var(--qn-foreground-muted)] mb-1 block">Monthly Cost Limit ($)</label>
                        <Input type="number" step="0.01" placeholder="e.g., 50.00" value={quotaCost} onChange={(e) => setQuotaCost(e.target.value)}
                          className="bg-[var(--qn-background)] border-[var(--qn-border)] h-8 text-xs" data-testid={`admin-quota-cost-${m.user_id}`} />
                      </div>
                    </div>
                    <Button size="sm" onClick={() => saveQuota(m.user_id)} disabled={saving} className="mt-3 h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-save-quota-${m.user_id}`}>
                      {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Save Quota
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

// ==================== Cost Allocation Tab ====================
function CostAllocationTab() {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/org/cost-allocation`).then(r => setAllocations(r.data.allocations || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-[var(--qn-foreground-muted)]" />Loading allocations...</div>;

  const barData = allocations.map((a) => ({
    name: (a.name || "?").split(" ")[0],
    haven: a.haven.cost,
    harvest: a.harvest.cost,
    other: a.other.cost,
  }));

  return (
    <div className="space-y-5" data-testid="admin-cost-allocation-tab">
      {barData.length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="admin-allocation-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">Cost Allocation by Member & Vertical</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <RTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} formatter={(v) => `$${Number(v).toFixed(4)}`} />
                <Bar dataKey="haven" name="HAVEN" stackId="a" fill={COLORS.haven} radius={[0, 0, 0, 0]} />
                <Bar dataKey="harvest" name="HARVEST" stackId="a" fill={COLORS.harvest} />
                <Bar dataKey="other" name="Custom" stackId="a" fill={COLORS.other} radius={[4, 4, 0, 0]} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: 10, color: "var(--qn-foreground-muted)" }}>{v}</span>} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--qn-border)]">
                <th className="text-left p-3 text-[var(--qn-foreground-muted)]">Member</th>
                <th className="text-right p-3 text-emerald-400">HAVEN</th>
                <th className="text-right p-3 text-amber-400">HARVEST</th>
                <th className="text-right p-3 text-indigo-400">Custom</th>
                <th className="text-right p-3 text-[var(--qn-foreground-muted)]">Total</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a) => (
                <tr key={a.user_id} className="border-b border-[var(--qn-border)]/30 hover:bg-[var(--qn-surface)]/20" data-testid={`admin-allocation-${a.user_id}`}>
                  <td className="p-3">
                    <div className="font-medium text-[var(--qn-foreground)]">{a.name}</div>
                    <div className="text-[10px] text-[var(--qn-foreground-muted)]">{a.email}</div>
                  </td>
                  <td className="text-right p-3 font-mono text-emerald-400">${a.haven.cost.toFixed(4)} <span className="text-[9px] text-[var(--qn-foreground-subtle)]">({a.haven.executions})</span></td>
                  <td className="text-right p-3 font-mono text-amber-400">${a.harvest.cost.toFixed(4)} <span className="text-[9px] text-[var(--qn-foreground-subtle)]">({a.harvest.executions})</span></td>
                  <td className="text-right p-3 font-mono text-indigo-400">${a.other.cost.toFixed(4)} <span className="text-[9px] text-[var(--qn-foreground-subtle)]">({a.other.executions})</span></td>
                  <td className="text-right p-3 font-mono font-bold text-[var(--qn-foreground)]">${a.total_cost.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Budget Tab ====================
function BudgetTab({ budget, onRefresh }) {
  const [monthlyBudget, setMonthlyBudget] = useState(budget?.monthly_budget?.toString() || "");
  const [thresholds, setThresholds] = useState((budget?.budget?.alert_thresholds || [50, 75, 90]).join(", "));
  const [autoPause, setAutoPause] = useState(budget?.auto_pause_at_limit || false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (budget) {
      setMonthlyBudget(budget.monthly_budget?.toString() || "");
      setThresholds((budget.budget?.alert_thresholds || [50, 75, 90]).join(", "));
      setAutoPause(budget.auto_pause_at_limit || false);
    }
  }, [budget]);

  const saveBudget = async () => {
    // Validate budget
    const budgetValue = parseFloat(monthlyBudget);
    if (isNaN(budgetValue) || budgetValue <= 0) {
      toast.error("Budget must be a positive number");
      return;
    }

    // Validate thresholds
    const parsedThresholds = thresholds.split(",").map(t => parseInt(t.trim())).filter(t => !isNaN(t) && t > 0 && t <= 100);
    if (parsedThresholds.length === 0) {
      toast.error("Please provide at least one valid alert threshold (1-100)");
      return;
    }
    if (parsedThresholds.length > 10) {
      toast.error("Maximum 10 alert thresholds allowed");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/org/budget`, {
        monthly_budget: budgetValue,
        alert_thresholds: parsedThresholds,
        auto_pause_at_limit: autoPause,
      });
      toast.success("Budget settings saved successfully");
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save budget");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="admin-budget-tab">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-400" />Monthly Budget</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-[var(--qn-foreground-muted)] mb-1.5 block">Monthly Budget Limit ($)</label>
            <Input type="number" step="0.01" placeholder="e.g., 500.00" value={monthlyBudget} onChange={(e) => setMonthlyBudget(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)] max-w-xs" data-testid="admin-budget-input" />
          </div>
          <div>
            <label className="text-xs text-[var(--qn-foreground-muted)] mb-1.5 block">Alert Thresholds (% of budget, comma-separated)</label>
            <Input placeholder="50, 75, 90" value={thresholds} onChange={(e) => setThresholds(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)] max-w-xs" data-testid="admin-thresholds-input" />
            <p className="text-[10px] text-[var(--qn-foreground-subtle)] mt-1">Alerts fire when org spend crosses these % levels</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={autoPause} onCheckedChange={setAutoPause} data-testid="admin-auto-pause-switch" />
            <div>
              <span className="text-xs text-[var(--qn-foreground)]">Auto-pause at budget limit</span>
              <p className="text-[10px] text-[var(--qn-foreground-muted)]">Automatically pause new executions when monthly spend hits the budget limit</p>
            </div>
          </div>
          <Button onClick={saveBudget} disabled={saving || !monthlyBudget} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-save-budget-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Budget Settings
          </Button>
        </CardContent>
      </Card>

      {budget?.monthly_budget > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-blue-400" />Current Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[var(--qn-foreground)]">${budget.current_spend.toLocaleString("en-US", { minimumFractionDigits: 2 })} spent of ${budget.monthly_budget.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              <span className="text-sm font-bold" style={{ color: budget.utilization_pct >= 90 ? "#ef4444" : budget.utilization_pct >= 75 ? "#f59e0b" : "#10b981" }}>
                {budget.utilization_pct}%
              </span>
            </div>
            <Progress value={Math.min(budget.utilization_pct, 100)} className="h-3" />
            <div className="mt-3 space-y-1.5">
              {(budget.budget?.alert_thresholds || [50, 75, 90]).map((t) => (
                <div key={t} className="flex items-center gap-2 text-xs">
                  {budget.utilization_pct >= t
                    ? <AlertTriangle className="w-3 h-3 text-amber-400" />
                    : <CheckCircle className="w-3 h-3 text-emerald-400" />}
                  <span className={budget.utilization_pct >= t ? "text-amber-400" : "text-[var(--qn-foreground-muted)]"}>
                    {t}% threshold {budget.utilization_pct >= t ? "— TRIGGERED" : "— OK"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== Main Component ====================
export default function TenantAdminPanel() {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ovRes, budRes] = await Promise.all([
        axios.get(`${API}/admin/org/overview`),
        axios.get(`${API}/admin/org/budget`),
      ]);
      setOverview(ovRes.data);
      setBudget(budRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin or owner role required");
        setError("You don't have permission to access this panel");
      } else if (e.response?.status === 401) {
        setError("Authentication required. Please log in again.");
      } else {
        setError(e.response?.data?.detail || "Failed to load tenant admin data");
        toast.error("Failed to load data");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const role = user?.role || "viewer";
  const isAdmin = role === "admin" || role === "owner";

  if (!isAdmin) {
    return (
      <div className="text-center py-24" data-testid="tenant-admin-panel">
        <Shield className="w-12 h-12 text-red-400/30 mx-auto mb-4" />
        <h2 className="text-lg font-outfit font-bold text-[var(--qn-foreground)]">Access Restricted</h2>
        <p className="text-sm text-[var(--qn-foreground-muted)] mt-2">Only admin and owner roles can access the Tenant Admin Panel.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-admin-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)] flex items-center gap-3">
            <Shield className="w-6 h-6 text-purple-400" /> Tenant Admin Panel
          </h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">
            Manage quotas, budgets, and cost allocation across your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-400">SOC 1 / SOC 2</Badge>
          <Button variant="ghost" size="sm" onClick={fetchData} className="h-8"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)]">
          <TabsTrigger value="overview" data-testid="admin-tab-overview"><BarChart3 className="w-4 h-4 mr-2" />Overview</TabsTrigger>
          <TabsTrigger value="members" data-testid="admin-tab-members"><Users className="w-4 h-4 mr-2" />Members</TabsTrigger>
          <TabsTrigger value="allocation" data-testid="admin-tab-allocation"><PieChartIcon className="w-4 h-4 mr-2" />Cost Allocation</TabsTrigger>
          <TabsTrigger value="budget" data-testid="admin-tab-budget"><DollarSign className="w-4 h-4 mr-2" />Budget</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab overview={overview} budget={budget} onRefresh={fetchData} loading={loading} error={error} />
        </TabsContent>
        <TabsContent value="members">
          <MembersTab onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="allocation">
          <CostAllocationTab />
        </TabsContent>
        <TabsContent value="budget">
          <BudgetTab budget={budget} onRefresh={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
